import type { ElementType, ReactNode, HTMLAttributes } from 'react';

// Semantic type scale matching the font sizes/weights already used across
// the app (page titles, section headers, table/body text, field labels,
// helper captions). Renders the appropriate tag per variant, with `color`
// and `className` as escape hatches for one-off overrides.
const VARIANTS = {
  h1: { tag: 'h1', className: 'text-2xl font-semibold text-gray-900' },
  h2: { tag: 'h2', className: 'text-lg font-semibold text-gray-900' },
  h3: { tag: 'h3', className: 'text-[16px] font-semibold text-gray-900' },
  sectionTitle: { tag: 'h2', className: 'text-[14px] font-semibold text-gray-800' },
  label: { tag: 'label', className: 'text-[13px] font-medium text-gray-700' },
  body: { tag: 'p', className: 'text-sm text-gray-700' },
  bodyStrong: { tag: 'p', className: 'text-sm font-medium text-gray-800' },
  caption: { tag: 'p', className: 'text-[11px] text-gray-400' },
  helper: { tag: 'p', className: 'text-xs text-gray-400' },
} as const satisfies Record<string, { tag: ElementType; className: string }>;

export type TypographyVariant = keyof typeof VARIANTS;
export type TypographyColor = 'default' | 'muted' | 'danger' | 'brand';

const COLOR_CLASS: Record<TypographyColor, string> = {
  default: '',
  muted: 'text-gray-400',
  danger: 'text-red-500',
  brand: 'text-[#3F707D]',
};

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}

export const Typography = ({ variant = 'body', color = 'default', as, className = '', children, ...rest }: TypographyProps) => {
  const { tag, className: variantClass } = VARIANTS[variant] ?? VARIANTS.body;
  const Tag = as ?? tag;
  return (
    <Tag className={`${variantClass} ${COLOR_CLASS[color] ?? ''} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};
